/**
 * Standard UTM tracking parameters.
 *
 * We capture these from the landing URL and forward them to Pardot with the
 * guest estimate. They are persisted to sessionStorage on first sighting so
 * they survive in-app (SPA) navigation and refreshes — by the time the user
 * opens and submits the estimate modal the original `?utm_*` query string is
 * usually long gone from the address bar.
 */
export const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const;

export type UtmKey = (typeof UTM_KEYS)[number];
export type UtmParams = Partial<Record<UtmKey, string>>;

const STORAGE_KEY = 'trusscore_utm';

function readStored(): UtmParams {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as UtmParams) : {};
  } catch {
    return {};
  }
}

/**
 * Capture any UTM params present on the current URL, merging them into the
 * session-persisted set. First-touch wins — we never clobber an already-stored
 * value, so the original campaign that brought the user in is preserved even if
 * later navigation carries different (or no) UTMs. Safe to call repeatedly;
 * a no-op when the URL carries no new UTMs. Returns the full merged set.
 */
export function captureUtmParams(): UtmParams {
  const stored = readStored();
  let changed = false;
  try {
    const params = new URLSearchParams(window.location.search);
    for (const key of UTM_KEYS) {
      const value = params.get(key)?.trim();
      if (value && !stored[key]) {
        stored[key] = value;
        changed = true;
      }
    }
    if (changed) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch {
    /* sessionStorage / URL parsing unavailable — return what we have */
  }
  return stored;
}

/** The UTM params captured so far this session (reads storage, no URL parse). */
export function getUtmParams(): UtmParams {
  return readStored();
}
